import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import type { AgentSummary, FindingsDoc } from '../../types/agents';

// STD-015: server state for the Agents recent-findings table. apiService is
// imported here (a *.queries.ts file) per STD-016. Errors are silenced via
// skipGlobalErrorToast — the table degrades silently when an agent log is
// unavailable (mirrors the original per-agent .catch(() => []) behaviour).

export const agentFindingsKeys = {
  all: ['agentFindings'] as const,
  list: (agentNames: string[]) => [...agentFindingsKeys.all, [...agentNames].sort()] as const,
};

export function useRecentFindingsQuery(agents: AgentSummary[]) {
  return useQuery({
    queryKey: agentFindingsKeys.list(agents.map((a) => `${a.name}:${a.has_cycle_log}`)),
    meta: { skipGlobalErrorToast: true },
    queryFn: async (): Promise<FindingsDoc[]> => {
      const results = await Promise.all(
        agents.map((agent) =>
          agent.has_cycle_log
            ? apiService.getAgentFindings(agent.name).catch(() => [] as FindingsDoc[])
            : Promise.resolve([] as FindingsDoc[])
        )
      );
      const merged = results.flat();
      merged.sort((a, b) => new Date(b.mtime).getTime() - new Date(a.mtime).getTime());
      return merged.slice(0, 10);
    },
  });
}
